import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, TextField, Typography } from "@mui/material"
import { postCareerHeadquartersApply } from "api"
import { useTranslations } from "next-intl"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, useForm } from "react-hook-form"
import { FileIcon } from "ui"
import { generateSchema, sendCvClick, sendCvError, sendCvSuccess } from "utils"
import { regex } from "utils/FormSchema"
import * as yup from "yup"
import Loading from "../Common/Loading"
import FormResult from "./FormResult"

const StyledDiv = styled.div`
  background-color: #f5f6f8;
  height: fit-content;
  padding: 16px;
  ${(p) => p.theme.breakpoints.up("md")} {
    border: 1px solid #d1d5db;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0px 2px 8px 0px #26303d29;
  }
`

const FormContainer = styled.form`
  label {
    color: #051711 !important;
  }
  .Mui-error {
    font-size: 14px;
  }
`

const StyledWrapperForm = styled.div`
  padding-top: 32px;
`

const StyledInputDivs = styled.div`
  padding-top: 24px;
`

const StyledMarketingWrapper = styled.div`
  margin-top: 44px;
`

const HiddenInput = styled.input`
  display: none;
`

const CustomUploadButton = styled(Button)<{ hasError?: boolean }>`
  background-color: white;
  text-decoration: none;
  color: black;
  height: 58.8px;
  width: 100%;
  outline: ${(p) => (p.hasError ? "1px solid #C92418" : "1px solid #D1D5DB")};
  border-radius: 12px;
  justify-content: flex-start;
  padding-left: 20px;
  padding: 12px 24px 12px 24px;
  outline: none;
  border: 1px solid #d1d5db;
  border-style: dashed;
  display: flex;
  justify-content: space-between;
  &:hover {
    background-color: white;
    text-decoration: none;
    color: black;
  }
`

const StyledErrorText = styled("p")`
  line-height: 22px;
  font-weight: 300;
  position: absolute;
  bottom: -25px;
  margin: 0;
  font-size: 14px;
  left: 20px;
  color: #c92418;
`
const StyledButton = styled(Button)<{ $isDisabled: boolean }>`
  width: 100%;
  margin-top: 24px;
  border: ${(p) => (p.$isDisabled ? "1px solid #d1d5db" : "none")};
  color: #051711;
  background-color: ${(p) => (p.$isDisabled ? "transparent" : "#008761")};
`

const generatedSchema = generateSchema("he")

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

type Props = {
  jobId: string
  formRef: any
  jobTitle: string
  category: string
  tooltipText?: string
}

const SingleJobForm = ({
  jobId,
  formRef,
  category,
  jobTitle,
  tooltipText = "",
}: Props) => {
  const t = useTranslations("Forms")
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [pdf, setPdf] = useState<any>(null)
  const [baseString, setBaseString] = useState("")
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const [submitForm, setSubmitForm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isFormError, setIsFormError] = useState(false)
  const [pdfError, setPdfError] = useState(false) as any

  const schema = yup.object({
    fullName: generatedSchema.fields.fullname,
    phone: generatedSchema.fields.phone,
    email: yup
      .string()
      .test(
        "email-validation",
        "כתובת האימייל שהוזנה אינה תקינה",
        function (value) {
          if (!value) return true
          return regex.email.test(value)
        },
      )
      .optional(),
    pdfFile: generatedSchema.fields.pdfFile,
    mailingConsent: yup.boolean().optional(),
  })

  type FormData = yup.InferType<typeof schema>

  const {
    handleSubmit,
    control,
    reset,
    setError,
    register,
    clearErrors,
    formState: { errors, isSubmitted, isValid },
  } = useForm<FormData>({
    defaultValues: {
      fullName: "",
      phone: "",
      pdfFile: "",
      email: "",
    },
    resolver: yupResolver(schema),
    mode: "onChange",
  })
  const onSubmit = async (data: FormData) => {
    const gRecaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string
    reCaptchaRef?.current?.reset()
    setIsLoading(true)
    // Create FormData and append the required fields
    const formDataToSend = new FormData()
    formDataToSend.append("CareerNumber", jobId)
    formDataToSend.append("Name", data.fullName as string)
    formDataToSend.append("Phone", data.phone as string)
    formDataToSend.append("ReplyTo", data.email as string)
    formDataToSend.append("Cv", pdf?.[0])
    formDataToSend.append("RecaptchaResponse", gRecaptchaResponse)
    formDataToSend.append("Sid", window?.__ctm?.config?.sid || "-1")
    formDataToSend.append("Complaint", String(data.mailingConsent || false))

    try {
      const response = await postCareerHeadquartersApply(formDataToSend)
      if (response && response.data.body) {
        setSubmitForm(true)
        setIsFormError(false)
        sendCvSuccess(t("successMessage") || "", jobTitle || "", category || "")
      }
    } catch (error) {
      sendCvError(t("errorMessage") || "", jobTitle || "", category || "")
      setSubmitForm(true)
      setIsFormError(true)
      console.error("Error during form submission:", error)
    } finally {
      // Clear form data and local state after submission (success or error)
      reset()
      setPdf(null)
      setPdfError(false)
      clearErrors()
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      setIsLoading(false)
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const validFileTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
      ]
      if (!validFileTypes.includes(file.type)) {
        setError("pdfFile", {
          type: "manual",
          message: "ניתן לעלות קובץ עד משקל MB 1.5",
        })
        setPdfError(true)
        setPdf(null)
        return
      }
      if (file.size > 1.5 * 1024 * 1024) {
        setError("pdfFile", {
          type: "manual",
          message: "ניתן לעלות קובץ עד משקל MB 1.5",
        })
        setPdfError(true)

        setPdf(null)
      } else {
        clearErrors("pdfFile")
        setPdfError(false)

        setPdf(e.target.files)
      }
    }
  }

  return (
    <StyledDiv ref={formRef}>
      {isLoading ? (
        <Loading />
      ) : !submitForm ? (
        <>
          <Typography variant="h3">{t("jobApplication")}</Typography>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <StyledWrapperForm>
              <Typography variant="body2">
                {t("mandatoryDetails")}
                <span style={{ color: "#E00700" }}>*</span>
              </Typography>
              <StyledInputDivs>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("fullName")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      error={!!errors.fullName}
                      ref={register("fullName").ref}
                      helperText={errors.fullName?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("mobileNumber")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      ref={register("phone").ref}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("mobileOptional")}
                      type="text"
                      variant="filled"
                      ref={register("email").ref}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="pdfFile"
                  control={control}
                  render={({ field }) => (
                    <>
                      <HiddenInput
                        {...field}
                        value={undefined}
                        ref={(e) => {
                          field.ref(e)
                          fileInputRef.current = e
                          register("pdfFile").ref
                        }}
                        type="file"
                        accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/plain"
                        onChange={(e) => {
                          handleFileUpload(e)
                          field.onChange(e)
                        }}
                      />
                      <CustomUploadButton
                        onClick={() => fileInputRef.current?.click()}
                        hasError={errors?.pdfFile ? true : false}
                        style={{
                          borderColor:
                            errors?.pdfFile || pdfError ? "#C92418" : "#D1D5DB",
                        }}
                      >
                        <Typography variant={"body1"}>
                          {pdf && pdf?.[0] ? (
                            pdf?.[0]?.name
                          ) : (
                            <>
                              {t("uploadCV")}
                              <span style={{ color: "#E00700" }}>*</span>
                            </>
                          )}
                        </Typography>

                        <StyledErrorText
                          style={{
                            color:
                              errors?.pdfFile || pdfError
                                ? "#C92418"
                                : "#051711",
                          }}
                        >
                          {t("maxFileSize")}
                        </StyledErrorText>

                        <FileIcon />
                      </CustomUploadButton>
                    </>
                  )}
                />
              </StyledInputDivs>
              <StyledMarketingWrapper></StyledMarketingWrapper>
              <StyledButton
                variant="primary"
                type="submit"
                $isDisabled={!isValid || pdfError}
                onClick={() => {
                  sendCvClick(
                    t("submitCV") || "",
                    jobTitle || "",
                    category || "",
                  )
                }}
              >
                {t("submitCV")}
              </StyledButton>
            </StyledWrapperForm>
          </FormContainer>
          {RECAPTCHA_KEY && (
            <ReCAPTCHA
              ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
              sitekey={RECAPTCHA_KEY}
              size="invisible"
              hl="iw"
              tabIndex={-1}
              badge="bottomright"
            />
          )}
        </>
      ) : (
        <FormResult
          isFormError={isFormError}
          setSubmitForm={setSubmitForm}
          setIsFormError={setIsFormError}
        />
      )}
    </StyledDiv>
  )
}

export default SingleJobForm
